import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Input } from "../../components/ui/input2";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";

import { FormField } from "../../types/forms";

interface ContactSectionProps {
  userId: string;
  templateId: string;
  isEditable?: boolean;
  readOnly?: boolean;
  fields?: FormField[];
  value?: Record<string, string>;
  onChange?: (value: Record<string, string>) => void;
}

interface ContactField {
  id: string;
  label: string;
  value: string;
  type?: string;
  options?: any;
}

export default function ContactSection({
  userId,
  templateId,
  isEditable = false,
  readOnly = false,
  fields,
  value,
  onChange,
}: ContactSectionProps) {
  const storageKey = `${userId}_${templateId}_contactfields`;

  const [storageFields, setFields] = useLocalStorage<ContactField[]>(storageKey, [
    { id: crypto.randomUUID(), label: "First Name", value: "" },
    { id: crypto.randomUUID(), label: "Last Name", value: "" },
    { id: crypto.randomUUID(), label: "Email", value: "" },
  ]);

  const [availableFields] = useState<string[]>([
    "First Name",
    "Last Name",
    "Email",
    "Phone Number",
    "Address",
    "City",
    "Country",
    "Company",
    "Job Title",
    "Website",
    "Postal Code",
    "Date of Birth",
  ]);

  const [message, setMessage] = useState<{ text: string; type: "success" | "error" | "" }>({
    text: "",
    type: "",
  });

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (message.text) {
      setVisible(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => setMessage({ text: "", type: "" }), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleAddField = (label: string) => {
    if (storageFields.some((f) => f.label === label)) {
      setMessage({ text: "This field is already added.", type: "error" });
      return;
    }
    setFields([...storageFields, { id: crypto.randomUUID(), label, value: "" }]);
  };

  const handleDeleteField = (id: string) => {
    setFields(storageFields.filter((f) => f.id !== id));
  };

  const countries = [
    "United Kingdom", "United States", "Canada", "Australia", "Ireland",
    "France", "Germany", "Spain", "Italy", "Netherlands", "Others"
  ];

  const getInputType = (label: string) => {
    const l = label.toLowerCase();
    if (l.includes("email")) return "email";
    if (l.includes("phone") || l.includes("mobile") || l.includes("landline")) return "tel";
    if (l.includes("date of birth") || l.includes("dob")) return "date";
    if (l.includes("website") || l.includes("url")) return "url";
    return "text";
  };

  const displayFields = (fields && fields.length > 0) ? fields.map(f => ({
    id: String(f.id),
    label: f.label,
    type: f.type,
    options: f.options,
    required: f.required,
    value: value?.[String(f.id)] || ""
  })) : storageFields.map(f => ({
    ...f,
    required: false,
    value: value?.[f.id] || f.value || ""
  }));

  const handleChangeInternal = (fieldId: string, val: string) => {
    if (onChange) {
      const newValues = { ...value, [fieldId]: val };
      onChange(newValues);
    } else {
      // Fallback for editor usage
      setFields(storageFields.map((f) => (f.id === fieldId ? { ...f, value: val } : f)));
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-[15px] font-semibold text-gray-800 mb-2">
        Contact Information
      </h2>
      <div className="space-y-3">
        {displayFields.length > 0 ? (
          displayFields.map((field) => (
            <div key={field.id} className="flex items-center gap-2">
              <div className="flex-1">
                <label className="block text-[13px] font-semibold text-gray-700 mb-1.5 ml-0.5">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1 font-bold">*</span>}
                </label>
                {field.type === "select" || field.label.toLowerCase() === "country" ? (
                  <Select onValueChange={(val) => handleChangeInternal(field.id, val)} value={field.value} disabled={readOnly}>
                    <SelectTrigger className="h-10 text-sm w-full border-gray-200 bg-gray-50/30 transition-all focus:ring-2 focus:ring-purple-100 hover:border-purple-200">
                      <SelectValue placeholder={`Select ${field.label}`} />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {(field.options || (field.label.toLowerCase() === "country" ? countries : [])).map((opt: string) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type={field.type && field.type !== "text" ? field.type : getInputType(field.label)}
                    value={field.value}
                    onChange={(e) => handleChangeInternal(field.id, e.target.value)}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    className="h-10 text-sm border-gray-200 bg-gray-50/30 transition-all focus:ring-2 focus:ring-purple-100 hover:border-purple-200"
                    disabled={readOnly}
                  />
                )}
              </div>
              {isEditable && !fields && (
                <button
                  onClick={() => handleDeleteField(field.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-400 italic text-center py-2">
            No fields added yet.
          </p>
        )}
      </div>
      {isEditable && !fields && !readOnly && (
        <div className="border-t border-gray-200 pt-3 space-y-2">
          {message.text && (
            <div
              className={`text-xs font-medium px-2 py-1 rounded-md transition-all duration-300 ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1"
                } ${message.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
                }`}
            >
              {message.text}
            </div>
          )}

          <label className="block text-xs font-medium text-gray-600">
            Add New Field
          </label>

          <div className="flex items-center gap-2">
            <Select onValueChange={(value) => handleAddField(value)}>
              <SelectTrigger className="flex-1 h-9 text-sm border-gray-300 bg-gray-50">
                <SelectValue placeholder="Choose a field to add..." />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {availableFields.map((label) => (
                  <SelectItem key={label} value={label}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <button
              onClick={() =>
                setMessage({ text: "Please select a field to add.", type: "error" })
              }
              className="p-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white transition-all"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
